import { z } from "zod";

import { DiscountAppDtoSchema } from "./discount-apps";
import { DiscountTierDtoSchema } from "./discount-tiers";
import { OfferProductDtoSchema } from "./offer-products";
import { UserInterfaceDtoSchema } from "./user-interfaces";

export const DiscountUnitTypeList = ["unit", "dollar"] as const;
export type DiscountUnitType = (typeof DiscountUnitTypeList)[number];

export const DiscountDiscountTypeList = ["percentage", "dollar"] as const;
export type DiscountDiscountType = (typeof DiscountDiscountTypeList)[number];

export const DiscountSettingFieldList = ["unitType", "discountType"] as const;
export type DiscountSettingFieldType =
  (typeof DiscountSettingFieldList)[number];

export const DiscountApplicationMethodList = ["perItem", "perOrder"] as const;
export type DiscountApplicationMethodType =
  (typeof DiscountApplicationMethodList)[number];

export const DiscountApplicationTypeList = [
  "tieredDiscount",
  "cartDiscount",
  "freeGift",
  "freeShipping",
  "",
] as const;
export type DiscountApplicationType =
  (typeof DiscountApplicationTypeList)[number];

export const DiscountApplicationTypeListNotEmpty = [
  "tieredDiscount",
  "cartDiscount",
  "freeGift",
  "freeShipping",
] as const;

export type DiscountApplicationTypeNotEmpty =
  (typeof DiscountApplicationTypeListNotEmpty)[number];

export const OfferDiscountLikeObjectSchema = z.object({
  id: z.string().uuid(),
  enabled: z.boolean().default(false),
  isTest: z.boolean().default(false),
  isArchived: z.boolean().default(false),
  name: z.string(),
  // offerProducts: OfferProductDtoSchema.array().optional().default([]),
  unitType: z.enum(DiscountUnitTypeList).default("dollar"),
  discountType: z.enum(DiscountDiscountTypeList).default("dollar"),
  tiers: DiscountTierDtoSchema.array().default([]),
  maximumDollarDiscount: z.number().nullish(),
  discountApplicationType: z.enum(DiscountApplicationTypeList),
  discountApplicationMethod: z.enum(DiscountApplicationMethodList),
  shouldStack: z.boolean(),
  shouldRejectCodes: z.boolean(),
});

export const OfferDtoSchema = z.object({
  id: z.string().uuid(),
  variationId: z.string().uuid(),
  enabled: z.boolean().default(false),
  isTest: z.boolean().default(false),
  isArchived: z.boolean().default(false),
  name: z.string(),
  // offerProducts: OfferProductDtoSchema.array().optional().default([]),
  unitType: z.enum(DiscountUnitTypeList).default("dollar"),
  discountType: z.enum(DiscountDiscountTypeList).default("dollar"),
  tiers: DiscountTierDtoSchema.array().default([]),
  maximumDollarDiscount: z.coerce.number().nullish(),
  discountApp: DiscountAppDtoSchema.nullable(),
  discountApplicationType: z.enum(DiscountApplicationTypeList),
  discountApplicationMethod: z.enum(DiscountApplicationMethodList),
  shouldStack: z.boolean(),
  shouldRejectCodes: z.boolean(),
});

export type OfferDto = z.infer<typeof OfferDtoSchema>;

export const OfferWithIdSchema = OfferDtoSchema.extend({
  userInterfaces: UserInterfaceDtoSchema.array().optional(),
});

export type OfferWithIdType = z.infer<typeof OfferWithIdSchema>;

export const CreateOfferDtoSchema = OfferDtoSchema.extend({
  id: z.string().optional(),
}).omit({ tiers: true });

export type CreateOfferDto = z.infer<typeof CreateOfferDtoSchema>;

export const PatchOfferDtoSchema = z.object({
  id: z.string().uuid().optional(),
  enabled: z.boolean().default(false),
  isTest: z.boolean().default(false),
  isArchived: z.boolean().default(false),
  name: z.string(),
  unitType: z.enum(DiscountUnitTypeList).default("dollar"),
  discountType: z.enum(DiscountDiscountTypeList).default("dollar"),
  maximumDollarDiscount: z.number().nullish(),
  discountApplicationType: z.enum(DiscountApplicationTypeList),
  discountApplicationMethod: z.enum(DiscountApplicationMethodList),
  shouldStack: z.boolean(),
  shouldRejectCodes: z.boolean(),
});

export type PatchOfferDto = z.infer<typeof PatchOfferDtoSchema>;
